import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, IconButton, InputLabel, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import React, { memo, useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { CTM_Colors, IRunData, jsonFields, validateRunData } from '../../Helper';
import pb from '../../pocketBase';

interface AddRunDialogProps {
  open: boolean;
  onClose: () => void;
  mode: 'create' | 'edit';
  runId?: string;
  initialData: IRunData;    // For create mode (copy last run) or edit mode (existing data)
  onRunSaved?: (id: string) => void;
}
interface CTM_FormControlProps {
  displayName: string;
  name: keyof IRunData;
  value: string;
  options: string[];
}

// colorHex is the color for each category
const sx_border = (colorHex: string) => ({
  border: `2px solid ${colorHex}`,
  borderRadius: 2,
  padding: 2,
  '& > *:not(:last-child)': {
    marginBottom: 2, // adds margin botton to each child comp (except the last one)
  },
});

interface SoftwareCategoryProps {
  software: any;
  software_params: any;
  onSoftwareChange: (val: any) => void;
  onParamsChange: (val: any) => void;
  onDeviceRemove: (device: string) => void;
}

const DeviceParamsEditor = memo(({ device, params, softwareParamsAsObj, onParamsChange }: {
  device: string;
  params: any;
  softwareParamsAsObj: Record<string, any>;
  onParamsChange: (val: any) => void;
}) => {
  const [text, setText] = useState(() => {
    if (!params) return '';
    if (typeof params === 'object') return Object.keys(params).length > 0 ? JSON.stringify(params, null, 2) : '';
    return String(params);
  });

  const handleBlur = () => {
    if (!text.trim()) {
      onParamsChange({ ...softwareParamsAsObj, [device]: {} });
      return;
    }
    try {
      onParamsChange({ ...softwareParamsAsObj, [device]: JSON.parse(text) });
    } catch {
      onParamsChange({ ...softwareParamsAsObj, [device]: text });
    }
  };

  return (
    <TextField
      label="Params (JSON or plain text)"
      fullWidth
      size="small"
      multiline
      rows={3}
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={handleBlur}
      inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.75rem' } }}
      sx={{ mt: 1 }}
    />
  );
});

const SoftwareCategory = memo(({ software, software_params, onSoftwareChange, onParamsChange, onDeviceRemove }: SoftwareCategoryProps) => {
  const [newDeviceName, setNewDeviceName] = useState('');

  const softwareAsObj: Record<string, any> = (typeof software === 'object' && software) ? software : {};
  const softwareParamsAsObj: Record<string, any> = (typeof software_params === 'object' && software_params) ? software_params : {};
  const devices = Object.entries(softwareAsObj);

  const handleAddDevice = () => {
    const name = newDeviceName.trim();
    if (!name || softwareAsObj[name] !== undefined) return;
    onSoftwareChange({ ...softwareAsObj, [name]: '' });
    onParamsChange({ ...softwareParamsAsObj, [name]: {} });
    setNewDeviceName('');
  };

  return (
    <Box sx={sx_border('#228B22')}>
      <Typography variant="h6" sx={{ mb: 1 }}>Software</Typography>

      {devices.length === 0 && (
        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
          No devices added yet. Add one below.
        </Typography>
      )}

      {devices.map(([device, version]) => {
        const params = softwareParamsAsObj[device];
        return (
          <Box key={device} sx={{ border: '1px solid #228B2260', borderRadius: 1, p: 1.5, mb: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <Typography sx={{ fontWeight: 'bold', fontSize: '0.85rem', color: '#228B22' }}>
                {device}
              </Typography>
              <IconButton size="small" onClick={() => onDeviceRemove(device)} sx={{ color: 'error.main', p: 0.25 }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            <TextField
              label="SW Version"
              fullWidth
              size="small"
              value={String(version)}
              onChange={(e) => onSoftwareChange({ ...softwareAsObj, [device]: e.target.value })}
              sx={{ mb: 0 }}
              inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
            />
            <DeviceParamsEditor
              device={device}
              params={params}
              softwareParamsAsObj={softwareParamsAsObj}
              onParamsChange={onParamsChange}
            />
          </Box>
        );
      })}

      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
        <TextField
          size="small"
          placeholder="Device name (e.g. vdcu)"
          value={newDeviceName}
          onChange={(e) => setNewDeviceName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddDevice(); } }}
          fullWidth
        />
        <Button variant="outlined" onClick={handleAddDevice} sx={{ whiteSpace: 'nowrap', color: '#228B22', borderColor: '#228B22' }}>
          + Add device
        </Button>
      </Stack>
    </Box>
  );
});

const AddRunDialog: React.FC<AddRunDialogProps> = ({ open, onClose, mode, runId, initialData, onRunSaved }) => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const [formData, setFormData] = useState<IRunData>(initialData);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      // Reset submitting state when dialog opens
      setIsSubmitting(false);
    }
  }, [open]);

  useEffect(() => {
    if (initialData) {
      const parsedData = { ...initialData };

      // default values for all the array fields
      const defaultArrayValues = {
        tire_pressure: new Array(4).fill(0),
        camber_IA: new Array(4).fill(0),
        tire_temperature: new Array(4).fill(0),
        toe: new Array(4).fill(0),
        springs: new Array(4).fill(0),
        damper_rebound: new Array(2).fill(0),
        damper_compression: new Array(2).fill(0),
        corner_weight: new Array(4).fill(0)
      };

      // parse array fields otherwise set default values
      Object.keys(defaultArrayValues).forEach((field) => {
        const key = field as keyof typeof defaultArrayValues;
        const value = parsedData[key];
        if (typeof value === 'string') {
          try {
            parsedData[key] = JSON.parse(value);
          } catch (error) {
            console.warn(`Failed to parse ${field}, using default values`);
            parsedData[key] = defaultArrayValues[key];
          }
        } else if (!Array.isArray(value)) {
          parsedData[key] = defaultArrayValues[key];
        }
      });
      // Normalize software fields to objects
      if (!parsedData.software || typeof parsedData.software === 'string') {
        (parsedData as any).software = {};
      }
      if (!parsedData.software_params || typeof parsedData.software_params === 'string') {
        (parsedData as any).software_params = {};
      }

      setFormData(parsedData);
    }
  }, [initialData]);

  const handleInputChange = (key: keyof IRunData, value: any) => {
    if (typeof value === 'string' && value === '') {
      // allow empty Strings  for textfields
      setFormData((prev) => ({ ...prev, [key]: '' }));
    } else if (typeof value === 'number' || !isNaN(Number(value))) {
      // convert all numbers if possible
      setFormData((prev) => ({ ...prev, [key]: Number(value) }));
    } else {
      setFormData((prev) => ({ ...prev, [key]: value }));
    }
  };

  const handleArrayChange = (
    key: keyof IRunData,
    index: number,
    value: any
  ) => {
    setFormData((prev) => {
      const newArray: any[] = Array.isArray(prev[key]) ? [...(prev[key] as number[])] : [];
      const num = Number(value);
      newArray[index] = value === '' ? '' : (isNaN(num) ? '' : num);
      return { ...prev, [key]: newArray as any };
    });
  };

  const handleSubmit = async () => {
    // Prevent double submission
    if (isSubmitting) return;

    // Validate form data before submitting
    const validation = validateRunData(formData);
    if (!validation.isValid) {
      validation.errors.forEach(error => toast.error(error));
      return;
    }

    setIsSubmitting(true);

    const dataToSubmit: { [key: string]: any } = mode === 'create' ? { sessionId: sessionId } : {};

    // remove keys that are created from db
    const runDataKeys = Object.keys(formData).filter((key): key is keyof IRunData =>
      key in formData && key !== 'id' && key !== 'collectionId' && key !== 'collectionName' && key !== 'created' && key !== 'updated'
    );

    // only IRunData fields
    runDataKeys.forEach((key) => {
      if (jsonFields.includes(key)) {
        // Convert arrays to json strings
          dataToSubmit[key] = JSON.stringify(formData[key]);
      } else {
        dataToSubmit[key] = formData[key];
      }
    });

    try {
      if (mode === 'create') {
        // CREATE new run
        const newRecord = await pb.collection('Run').create(dataToSubmit);
        toast.success('Run created successfully!');
        onRunSaved?.(newRecord.id);
      } else {
        // UPDATE existing run
        if (!runId) {
          toast.error('No run ID provided for editing');
          setIsSubmitting(false);
          return;
        }
        const updatedRecord = await pb.collection('Run').update(runId, dataToSubmit);
        toast.success('Run updated successfully!');
        onRunSaved?.(updatedRecord.id);
      }
      onClose();
    } catch (error) {
      const errorMessage = mode === 'create'
        ? 'Error creating run: '
        : 'Error updating run: ';
      toast.error(errorMessage + error);
      setIsSubmitting(false);
    }
  };

  const CTM_FormControl: React.FC<CTM_FormControlProps> = ({ name, value, displayName, options }) => {
    return (
      <FormControl fullWidth>
        <InputLabel>{displayName}</InputLabel>
        <Select
          label={displayName}
          value={value}
          onChange={(e) =>
            handleInputChange(
              name,
              e.target.value as IRunData[typeof name]
            )
          }
        >
          {options.map((option) => (
            <MenuItem key={option} value={option}>{option}</MenuItem>
          ))}
        </Select>
      </FormControl>
    )
  }  

  const aeroCategory = () => {
    return (
      <Box sx={sx_border('#1976d2')}>
        <Typography variant="h6">Aero</Typography>
        <CTM_FormControl name="undertray" value={formData.undertray} displayName='Undertray' options={['On', 'Off']} />
        <CTM_FormControl name="frontwing" value={formData.frontwing} displayName='Frontwing' options={['On','High Drag', 'Low Drag', 'Off']} />
        <CTM_FormControl name="rearwing" value={formData.rearwing} displayName='Rearwing' options={['On','High Drag', 'Low Drag', 'Off']} />
      </Box>
    )
  }

  const generalCategory = () => {
    return (
      <Box sx={sx_border('#B22222')}>
        <Typography variant="h6">General</Typography>
        <CTM_FormControl name="event" value={formData.event} displayName='Event' options={['autocross', 'endurance', 'half-skidpad clockwise', 'half-skidpad counterclockwise', 'acceleration', 'skipad event']} />
        <TextField
          label="Driver"
          fullWidth
          value={formData.driver}
          onChange={(e) => handleInputChange('driver', e.target.value)}
        />
        <TextField
            label="Comment"
            fullWidth
            margin="normal"
            value={formData.runComment || ''}
            onChange={(e) => setFormData({...formData, runComment: e.target.value})}
            multiline
            rows={2}
        />
        <Stack direction="row" spacing={2}>
          <TextField
            label="Track Temperature (°C)"
            type="number"
            fullWidth
            value={formData.track_temperature}
            onChange={(e) =>
              handleInputChange(
                'track_temperature',
                e.target.value
              )
            }
          />
          <TextField
            label="Ambient Temperature (°C)"
            type="number"
            fullWidth
            value={formData.ambient_temperature}
            onChange={(e) =>
              handleInputChange(
                'ambient_temperature',
                e.target.value
              )
            }
          />
          <TextField
            label="Ambient Humidity (%)"
            type="number"
            fullWidth
            value={formData.ambient_humidity}
            onChange={(e) =>
              handleInputChange(
                'ambient_humidity',
                e.target.value
              )
            }
          />
          <TextField
            label="Ambient Pressure (hPa)"
            type="number"
            fullWidth
            value={formData.ambient_pressure}
            onChange={(e) =>
              handleInputChange(
                'ambient_pressure',
                e.target.value
              )
            }
          />
        </Stack>
        <CTM_FormControl name="sky_condition" value={formData.sky_condition} displayName='Sky Condition' options={['clear', 'cloudy', 'very cloudy']} />
        <Typography variant="body2" color="textSecondary">Precipitation</Typography>
        <Stack direction="row" spacing={2}>
          <CTM_FormControl name="precipitationType" value={formData.precipitationType} displayName='Precipitation Type' options={['none', 'rainy', 'snowy']} />
          <TextField
            label="Precipitation Amount"
            fullWidth
            value={formData.precipitationAmount}
            onChange={(e) =>
              handleInputChange('precipitationAmount', e.target.value)
            }
            helperText="e.g., light, moderate, heavy"
          />
        </Stack>
      </Box>
    )
  }

  const handleSoftwareChange = useCallback((val: any) => {
    setFormData((prev) => ({ ...prev, software: val }));
  }, []);

  const handleSoftwareParamsChange = useCallback((val: any) => {
    setFormData((prev) => ({ ...prev, software_params: val }));
  }, []);

  const handleDeviceRemove = useCallback((device: string) => {
    setFormData((prev) => {
      const sw: Record<string, any> = typeof prev.software === 'object' && prev.software ? { ...(prev.software as any) } : {};
      const sp: Record<string, any> = typeof prev.software_params === 'object' && prev.software_params ? { ...(prev.software_params as any) } : {};
      delete sw[device];
      delete sp[device];
      return { ...prev, software: sw as any, software_params: sp as any };
    });
  }, []);

  const suspensionCategory = () => {
    return (
      <Box sx={sx_border('#AA76d2')}>
        <Typography variant="h6">Suspension</Typography>
        <CTM_FormControl name="tire_compound" value={formData.tire_compound} displayName='Tire Compound' options={['R20', 'LC0', 'Wet']} />
        
        {/* Tire Pressure */}
        <Typography variant="subtitle1">
          Tire Pressure (bar)
        </Typography>
        <Stack direction="row" spacing={4}>
          {['Front Left', 'Front Right', 'Rear Left', 'Rear Right'].map((position, index) => (
            <TextField
              key={`tire_pressure-${position}`}
              label={position}
              type="number"
              fullWidth
              value={formData.tire_pressure[index]}
              onChange={(e) =>
                handleArrayChange(
                  'tire_pressure',
                  index,
                  e.target.value
                )
              }
            />
          ))}
        </Stack>
        
        {/* Camber IA */}
        <Typography variant="subtitle1">
          Camber IA (°)
        </Typography>
        <Stack direction="row" spacing={4}>
          {['Front Left', 'Front Right', 'Rear Left', 'Rear Right'].map((position, index) => (
            <TextField
              key={`camber_IA-${position}`}
              label={position}
              type="number"
              fullWidth
              value={formData.camber_IA[index]}
              onChange={(e) =>
                handleArrayChange(
                  'camber_IA',
                  index,
                  e.target.value
                )
              }
            />
          ))}
        </Stack>

        {/* Tire Temperature */}
        <Typography variant="subtitle1">
          Tire Temperature (°C)
        </Typography>
        <Stack direction="row" spacing={4}>
          {['Front Left', 'Front Right', 'Rear Left', 'Rear Right'].map((position, index) => (
            <TextField
              key={`tire_temperature-${position}`}
              label={position}
              type="number"
              fullWidth
              value={formData.tire_temperature[index]}
              onChange={(e) =>
                handleArrayChange(
                  'tire_temperature',
                  index,
                  e.target.value
                )
              }
            />
          ))}
        </Stack>

        {/* Toe */}
        <Typography variant="subtitle1">
          Toe (°C)
        </Typography>
        <Stack direction="row" spacing={4}>
          {['Front Left', 'Front Right', 'Rear Left', 'Rear Right'].map((position, index) => (
            <TextField
              key={`toe-${position}`}
              label={position}
              type="number"
              fullWidth
              value={formData.toe[index]}
              onChange={(e) =>
                handleArrayChange(
                  'toe',
                  index,
                  e.target.value
                )
              }
            />
          ))}
        </Stack>

        {/* Springs */}
        <Typography variant="subtitle1">
          Springs (kN)
        </Typography>
        <Stack direction="row" spacing={4}>
          {['Front Left', 'Front Right', 'Rear Left', 'Rear Right'].map((position, index) => (
            <TextField
              key={`springs-${position}`}
              label={position}
              type="number"
              fullWidth
              value={formData.springs[index]}
              onChange={(e) =>
                handleArrayChange(
                  'springs',
                  index,
                  e.target.value
                )
              }
            />
          ))}
        </Stack>
       
        {/* Damper Rebound */}
        <Typography variant="subtitle1">
          Damper Rebound (clicks)
        </Typography>
        <Stack direction="row" spacing={2}>
          {['Front', 'Rear'].map((position, index) => (
            <TextField
              key={`damperRebound-${position}`}
              label={position}
              type="number"
              fullWidth
              value={formData.damper_rebound[index]}
              onChange={(e) =>
                handleArrayChange(
                  'damper_rebound',
                  index,
                  e.target.value
                )
              }
            />
          ))}
        </Stack>

        {/* Damper Compression */}
        <Typography variant="subtitle1">
          Damper Compression (clicks)
        </Typography>
        <Stack direction="row" spacing={2}>
          {['Front', 'Rear'].map((position, index) => (
            <TextField
              key={`damperCompression-${position}`}
              label={position}
              type="number"
              fullWidth
              value={formData.damper_compression[index]}
              onChange={(e) =>
                handleArrayChange(
                  'damper_compression',
                  index,
                  e.target.value
                )
              }
            />
          ))}
        </Stack>

        {/* ARB Setting */}
        <TextField
          label="ARB Setting"
          fullWidth
          value={formData.arb_setting}
          onChange={(e) => handleInputChange('arb_setting', e.target.value)}
        />

        {/* Corner Weight */}
        <Typography variant="subtitle1">
          Corner Weight (kg)
        </Typography>
        <Stack direction="row" spacing={4}>
          {['Front Left', 'Front Right', 'Rear Left', 'Rear Right'].map((position, index) => (
            <TextField
              key={`corner_weight-${position}`}
              label={position}
              type="number"
              fullWidth
              value={formData.corner_weight[index]}
              onChange={(e) =>
                handleArrayChange(
                  'corner_weight',
                  index,
                  e.target.value
                )
              }
            />
          ))}
        </Stack>
      </Box>
    )
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ color: CTM_Colors.ctm_green }}>
        {mode === 'create' ? 'New Run Setup' : 'Edit Run Setup'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {aeroCategory()}
          {generalCategory()}
          <SoftwareCategory
            software={formData.software}
            software_params={formData.software_params}
            onSoftwareChange={handleSoftwareChange}
            onParamsChange={handleSoftwareParamsChange}
            onDeviceRemove={handleDeviceRemove}
          />
          {suspensionCategory()}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={isSubmitting}>Cancel</Button>
        <Button onClick={handleSubmit} color="primary" disabled={isSubmitting}>
          {isSubmitting ? 'Saving...' : (mode === 'create' ? 'Create Run' : 'Save Changes')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddRunDialog;